import * as pdfjsLib from "pdfjs-dist";
import pdfWorkerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Lazy, bounded thumbnail rendering with IndexedDB cache.
const MAX_CONCURRENT_RENDERS = 2;
const RENDER_SCALE = 0.6;
const JPEG_QUALITY = 0.8;

type PdfDocument = Awaited<ReturnType<typeof pdfjsLib.getDocument>["promise"]>;

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

// IndexedDB utilities (tiny, dependency-free).
const DB_NAME = "quick-slides-thumbs-v1";
const STORE = "thumbs";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "key" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbGet(key: string): Promise<Blob | null> {
  try {
    const db = await openDb();
    return await new Promise<Blob | null>((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const store = tx.objectStore(STORE);
      const r = store.get(key);
      r.onsuccess = () => resolve(r.result ? (r.result.blob as Blob) : null);
      r.onerror = () => reject(r.error);
    });
  } catch {
    return null;
  }
}

async function idbPut(key: string, blob: Blob): Promise<void> {
  try {
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const r = store.put({ key, blob });
      r.onsuccess = () => resolve();
      r.onerror = () => reject(r.error);
    });
  } catch {
    // swallow — cache best-effort only
  }
}

export class ThumbnailRenderer {
  private pdf: PdfDocument | null = null;
  private observer: IntersectionObserver | null = null;
  private objectUrls: string[] = [];
  private queue: Array<() => Promise<void>> = [];
  private active = 0;
  private disposed = false;

  // Metadata for cache keys.
  private itemId: string | null = null;
  private contentVersion: string | null = null;
  private renderVersion = 1;

  /**
   * Load a PDF and optional cache metadata. `meta` provides values used to
   * build the IndexedDB cache key: itemId, contentVersion and renderVersion.
   */
  async load(
    pdfBytes: ArrayBuffer,
    meta?: { itemId?: string; contentVersion?: string; renderVersion?: number }
  ): Promise<number> {
    this.itemId = meta?.itemId ?? null;
    this.contentVersion = meta?.contentVersion ?? null;
    this.renderVersion = meta?.renderVersion ?? 1;

    this.pdf = await pdfjsLib.getDocument({
      data: new Uint8Array(pdfBytes.slice(0)),
      disableAutoFetch: true,
      disableStream: false,
    }).promise;

    return this.pdf.numPages;
  }

  observe(card: HTMLElement, pageNumber: number, target: HTMLElement): void {
    if (this.disposed || !this.pdf) return;

    if (!this.observer) {
      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            const element = entry.target as HTMLElement;
            this.observer?.unobserve(element);

            const page = Number(element.dataset.pageNumber);
            const slot = element.querySelector<HTMLElement>(".slide-thumb");
            if (!page || !slot) continue;

            this.queue.push(() => this.renderPage(page, slot));
            void this.drain();
          }
        },
        { root: null, rootMargin: "200px 0px", threshold: 0.01 }
      );
    }

    card.dataset.pageNumber = String(pageNumber);
    void target;
    this.observer.observe(card);
  }

  private async drain(): Promise<void> {
    if (this.active >= MAX_CONCURRENT_RENDERS) return;

    const task = this.queue.shift();
    if (!task) return;

    this.active++;
    try {
      await task();
    } finally {
      this.active--;
      await nextFrame();
      void this.drain();
    }
  }

  private cacheKeyFor(pageNumber: number) {
    if (!this.itemId || !this.contentVersion) return null;
    return `${this.itemId}:${this.contentVersion}:${pageNumber}:${this.renderVersion}`;
  }

  private async renderPage(pageNumber: number, slot: HTMLElement): Promise<void> {
    if (this.disposed || !this.pdf || pageNumber > this.pdf.numPages) return;

    // Try IndexedDB first.
    const key = this.cacheKeyFor(pageNumber);
    if (key) {
      const cached = await idbGet(key).catch(() => null);
      if (cached) {
        const url = URL.createObjectURL(cached);
        this.objectUrls.push(url);

        const img = document.createElement("img");
        img.src = url;
        img.alt = `Slide ${pageNumber}`;
        img.loading = "lazy";
        img.decoding = "async";

        slot.classList.remove("is-loading");
        slot.textContent = "";
        slot.appendChild(img);
        return;
      }
    }

    try {
      const page = await this.pdf.getPage(pageNumber);
      const viewport = page.getViewport({ scale: RENDER_SCALE });

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));

      const canvasContext = canvas.getContext("2d", { alpha: false });
      if (!canvasContext) return;

      await page.render({ canvasContext, viewport }).promise;

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
      );

      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();

      if (!blob || this.disposed) return;

      // Store in IndexedDB (best-effort).
      if (key) void idbPut(key, blob);

      const url = URL.createObjectURL(blob);
      this.objectUrls.push(url);

      const img = document.createElement("img");
      img.src = url;
      img.alt = `Slide ${pageNumber}`;
      img.loading = "lazy";
      img.decoding = "async";

      slot.classList.remove("is-loading");
      slot.textContent = "";
      slot.appendChild(img);
    } catch {
      slot.classList.remove("is-loading");
      slot.classList.add("is-failed");
      slot.textContent = `Slide ${pageNumber}`;
    }
  }

  dispose(): void {
    this.disposed = true;
    this.queue = [];

    this.observer?.disconnect();
    this.observer = null;

    for (const url of this.objectUrls) URL.revokeObjectURL(url);
    this.objectUrls = [];

    void this.pdf?.destroy();
    this.pdf = null;
  }
}
